import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Text from '../../shared/uiComponents/Text';

import { filterByCompleted, filterByDrafts, sortByTime } from './Helpers/functions';
import { DayProperties } from './Helpers/interfaces';
import { weeks } from './Helpers/constants';
import Session from './Session';
import { parseWallClockTime } from '../../shared/Helpers/timezones';
import SessionHeader from './Session/sessionHeader';

import { DispatchProperties, useSelector } from '../../redux/store';
import { goToNextMonth, goToPreviousMonth, setSelectedDay } from '../../redux/State/clientSlice/calendarHelperSlice';
import { useScreenWidth } from '../../shared/Helpers/hooks';
import { useSwipe } from './hooks/useSwipe';

const MonthView = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const showCompletedSession = useSelector((state) => state.calendarHelper.filterData.showCompleted);
	const showDraftsOnly = useSelector((state) => state.calendarHelper.filterData.showDraftsOnly);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const events = useSelector((state) => state.calendar.events);
	const swipeHandlers = useSwipe({
		onSwipeLeft: () => {
			if (isMobile) {
				dispatch(goToNextMonth());
			}
		},
		onSwipeRight: () => {
			if (isMobile) {
				dispatch(goToPreviousMonth());
			}
		},
	});

	const eventsByDate = useMemo(() => {
		const groupedEvents = new Map<string, NonNullable<typeof events>>();
		const visibleEvents = [...(events ?? [])]
			.sort((a, b) => sortByTime({ a, b }))
			.filter((event) => (showCompletedSession ? filterByCompleted(event) : true))
			.filter((event) => (showDraftsOnly ? filterByDrafts(event) : true));

		visibleEvents.forEach((event) => {
			const startTime = parseWallClockTime(event.startTime);
			if (!startTime.isValid()) return;

			const key = `${startTime.year()}-${startTime.month()}-${startTime.date()}`;
			const dateEvents = groupedEvents.get(key) ?? [];
			dateEvents.push(event);
			groupedEvents.set(key, dateEvents);
		});

		return groupedEvents;
	}, [events, showCompletedSession, showDraftsOnly]);

	const daysMapper = (item: DayProperties, index: number) => {
		// eslint-disable-next-line
		const selectDayHandler = (item: DayProperties) => {
			dispatch(setSelectedDay(item));
		};

		const dateEvents = eventsByDate.get(`${item.year}-${item.month}-${item.day}`) ?? [];

		return (
			<div key={index} className="day" onClick={() => selectDayHandler(item)}>
				<SessionHeader item={item} dayIndex={index} />
				<div className="sessionWrapper">
					{dateEvents.map((event) => <Session key={event.id} event={event} />)}
				</div>
			</div>
		);
	};

	const weekNameTitleHandler = (weekName: string) => {
		if (!isMobile) return weekName;

		return weekName.slice(0, 2);
	};

	return (
		<div className="monthView" {...(isMobile ? swipeHandlers : {})}>
			<div className="headers">
				{weeks.map((day, index) => (
					<Text
						key={index}
						title={weekNameTitleHandler(day)}
						type={isMobile ? 'pxs' : 'psm'}
						fontWeight="medium"
						className="header"
					/>
				))}
			</div>
			<div className="daysWrapper">{days.map(daysMapper)}</div>
		</div>
	);
};

export default MonthView;
